import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import {
  SidebarWrapper,
  SidebarWrapperOverflowContainer,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeskId } from '@/hooks/useDeskId';
import useVersions from '@/hooks/useVersions';
import { NEW_RULE_ID } from '@/modules/rules/constants';
import { useRules } from '@/modules/rules/hooks/useRules';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { RenameRuleForm } from './RenameRuleForm';
import { RuleForm } from './RuleForm';
import { getNewRuleValues, toRuleFormValues } from './ruleFormSchema';

type RulePanelProps = {
  ruleId: string;
  isRename?: boolean;
  onClose: () => void;
  onOpenEdit: () => void;
};

export const RulePanel = ({ ruleId, isRename = false, onClose, onOpenEdit }: RulePanelProps) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const canWrite = useSelector((state: RootState) => getPermissions(state, 'rules', actions.WRITE));
  const [searchParams] = useSearchParams();

  const isCreate = ruleId === NEW_RULE_ID;
  const brainId = isCreate ? (searchParams.get('brainId') ?? undefined) : undefined;
  const { rule, getStatus } = useRules(deskId, isCreate ? undefined : ruleId);
  const { versionsWithoutDraft, getStatus: versionsStatus } = useVersions({
    brainId: brainId ?? '',
  });

  const isLoadFailed = !isCreate && getStatus === 'error';
  const isLoading =
    !isLoadFailed && (isCreate ? Boolean(brainId) && versionsStatus === 'pending' : !rule);

  const latestVersion = versionsWithoutDraft?.[0]?.version ?? 0;
  const defaultValues = isCreate
    ? getNewRuleValues(brainId ? { brainParentId: brainId, version: latestVersion } : undefined)
    : rule && toRuleFormValues(rule);

  const getTitle = () => {
    if (isCreate) {
      return t('rules.create_rule');
    }
    if (isRename) {
      return t('rules.rename_rule');
    }
    if (isLoadFailed) {
      return t('rules.edit_rule');
    }
    return rule?.name;
  };

  const title = getTitle();

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle className="gap-2 pr-4">
        {title ? (
          <TruncatedText text={title} className="text-base leading-none font-medium" />
        ) : (
          <Skeleton className="h-4 w-40" />
        )}
        <Button
          type="button"
          variant="ghost-neutral"
          size="icon-sm"
          aria-label={t('common.close')}
          onClick={onClose}
        >
          <XIcon size={16} className="text-foreground-muted" />
        </Button>
      </SidebarWrapperTitle>
      {isLoadFailed && (
        <SidebarWrapperOverflowContainer>
          <p className="text-sm text-foreground-muted">{t('rules.editor.load_failed')}</p>
        </SidebarWrapperOverflowContainer>
      )}
      {isLoading && (
        <SidebarWrapperOverflowContainer>
          <div className="flex flex-col gap-6">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </SidebarWrapperOverflowContainer>
      )}
      {!isLoadFailed && !isLoading && isRename && rule && (
        <RenameRuleForm rule={rule} onClose={onClose} onOpenEdit={onOpenEdit} />
      )}
      {!isLoadFailed && !isLoading && !isRename && (
        <RuleForm
          ruleId={isCreate ? undefined : ruleId}
          defaultValues={defaultValues}
          canWrite={canWrite}
          onClose={onClose}
        />
      )}
    </SidebarWrapper>
  );
};
